import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, XCircle, Loader } from 'lucide-react';
import { api } from '../lib/api';

const ACCENT = '#f97316';

interface ConnectorInfo {
  id: string;
  name: string;
  status: string;
  category: string;
  auth: { type: string; fields: { name: string; label: string; type: string; required: boolean }[] };
}

export default function NewConnectionPage() {
  const navigate = useNavigate();
  const [connectors, setConnectors] = useState<ConnectorInfo[]>([]);
  const [selectedConnector, setSelectedConnector] = useState('');
  const [name, setName] = useState('');
  const [config, setConfig] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    loadConnectors();
  }, []);

  async function loadConnectors() {
    try {
      const data = await api.get<{ connectors: ConnectorInfo[] }>('/connectors');
      setConnectors(data.connectors);
    } catch {}
  }

  const selected = connectors.find(c => c.id === selectedConnector);
  const authFields = selected?.auth?.fields || [];
  const isExternal = selectedConnector && selectedConnector !== 'csv';

  function updateConfig(fieldName: string, value: string) {
    setConfig(prev => ({ ...prev, [fieldName]: value }));
  }

  async function handleCreate() {
    if (!selectedConnector || !name) return;
    setSaving(true);
    setError('');
    try {
      const result = await api.post<{ connection: { id: string } }>('/connections', {
        connector_id: selectedConnector,
        name,
        config,
      });

      // If external connector, test the connection
      if (isExternal && result.connection?.id) {
        setTesting(true);
        const testRes = await api.post<{ success: boolean; message: string }>(`/connections/${result.connection.id}/test`);
        setTestResult(testRes);
        setTesting(false);
      }

      navigate('/connections');
    } catch (err: any) {
      setError(err.message);
    }
    setSaving(false);
  }

  async function handleTestOnly() {
    if (!selectedConnector || authFields.length === 0) return;
    setTesting(true);
    setTestResult(null);
    setError('');

    try {
      // Create a temp connection to test
      const result = await api.post<{ connection: { id: string } }>('/connections', {
        connector_id: selectedConnector,
        name: name || `Test - ${selected?.name}`,
        config,
      });

      const testRes = await api.post<{ success: boolean; message: string }>(`/connections/${result.connection.id}/test`);
      setTestResult(testRes);

      // If test failed, delete the temp connection
      if (!testRes.success) {
        await api.delete(`/connections/${result.connection.id}`).catch(() => {});
      }
    } catch (err: any) {
      setTestResult({ success: false, message: err.message });
    }
    setTesting(false);
  }

  const CONNECTOR_DESCRIPTIONS: Record<string, string> = {
    csv: 'Upload CSV files and map columns to any entity type',
    autotask: 'Import tickets, contacts, companies, time entries from Datto Autotask',
    connectwise: 'Import data from ConnectWise Manage (companies, tickets, configs, time)',
    it_glue: 'Import configurations, passwords, contacts, and flexible assets from IT Glue',
  };

  const CONNECTOR_ICONS: Record<string, string> = {
    csv: 'CSV',
    autotask: 'AT',
    connectwise: 'CW',
    it_glue: 'ITG',
  };

  return (
    <div>
      <button onClick={() => navigate('/connections')} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', border: 'none', backgroundColor: 'transparent', cursor: 'pointer', color: '#6b7280', marginBottom: '1rem', padding: 0, fontSize: '0.875rem' }}>
        <ArrowLeft size={16} /> Back to Connections
      </button>

      <h2 style={{ fontSize: '1.5rem', fontWeight: 700, margin: '0 0 1.5rem', color: '#111827' }}>New Connection</h2>

      {/* Connector Selection */}
      <div style={{ marginBottom: '1.5rem' }}>
        <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.5rem', fontSize: '0.875rem' }}>Select Connector</label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.75rem' }}>
          {connectors.map(c => (
            <button
              key={c.id}
              onClick={() => { setSelectedConnector(c.id); setConfig({}); setTestResult(null); setName(''); }}
              style={{
                padding: '1rem',
                borderRadius: '0.5rem',
                border: selectedConnector === c.id ? `2px solid ${ACCENT}` : '1px solid #e5e7eb',
                backgroundColor: selectedConnector === c.id ? '#fff7ed' : '#fff',
                cursor: 'pointer',
                textAlign: 'left',
                position: 'relative',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: '0.25rem', backgroundColor: ACCENT + '15', color: ACCENT, fontSize: '0.7rem', fontWeight: 700 }}>
                  {CONNECTOR_ICONS[c.id] || c.id.slice(0, 2).toUpperCase()}
                </span>
                <span style={{ fontWeight: 600, fontSize: '0.9375rem' }}>{c.name}</span>
              </div>
              <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{CONNECTOR_DESCRIPTIONS[c.id] || ''}</div>
              {c.status === 'beta' && (
                <span style={{ position: 'absolute', top: 8, right: 8, fontSize: '0.65rem', padding: '0.1rem 0.3rem', backgroundColor: '#fef3c7', color: '#92400e', borderRadius: '0.2rem', fontWeight: 500 }}>BETA</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Configuration Form */}
      {selectedConnector && (
        <div style={{ backgroundColor: '#fff', borderRadius: '0.5rem', border: '1px solid #e5e7eb', padding: '1.25rem' }}>
          <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.375rem', fontSize: '0.875rem' }}>Connection Name</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder={`e.g., My ${selected?.name || ''} Connection`}
            style={inputStyle}
          />

          {/* Auth Fields */}
          {authFields.length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              <h4 style={{ fontSize: '0.875rem', fontWeight: 600, margin: '0 0 0.75rem', color: '#374151' }}>API Credentials</h4>
              {authFields.map(field => (
                <div key={field.name} style={{ marginBottom: '0.75rem' }}>
                  <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.25rem', fontSize: '0.8rem' }}>
                    {field.label} {field.required && <span style={{ color: '#ef4444' }}>*</span>}
                  </label>
                  <input
                    type={field.type === 'password' ? 'password' : 'text'}
                    value={config[field.name] || ''}
                    onChange={e => updateConfig(field.name, e.target.value)}
                    placeholder={field.label}
                    style={inputStyle}
                  />
                </div>
              ))}

              {/* Test Connection */}
              <button
                onClick={handleTestOnly}
                disabled={testing}
                style={{ ...secondaryBtnStyle, marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                {testing ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : null}
                Test Connection
              </button>

              {testResult && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.75rem',
                  padding: '0.5rem 0.75rem', borderRadius: '0.375rem', fontSize: '0.8rem',
                  backgroundColor: testResult.success ? '#f0fdf4' : '#fef2f2',
                  color: testResult.success ? '#16a34a' : '#dc2626',
                  border: `1px solid ${testResult.success ? '#bbf7d0' : '#fecaca'}`,
                }}>
                  {testResult.success ? <CheckCircle size={16} /> : <XCircle size={16} />}
                  {testResult.message}
                </div>
              )}
            </div>
          )}

          {error && <p style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '0.5rem' }}>{error}</p>}

          <button
            onClick={handleCreate}
            disabled={!name || saving || (!!isExternal && authFields.some(f => f.required && !config[f.name]))}
            style={{
              ...primaryBtnStyle,
              marginTop: '1rem',
              opacity: name && !saving ? 1 : 0.5,
              cursor: name && !saving ? 'pointer' : 'not-allowed',
            }}
          >
            {saving ? 'Creating...' : 'Create Connection'}
          </button>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

const inputStyle: React.CSSProperties = { width: '100%', padding: '0.5rem 0.75rem', borderRadius: '0.375rem', border: '1px solid #d1d5db', fontSize: '0.875rem', boxSizing: 'border-box' };
const primaryBtnStyle: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1.25rem', backgroundColor: '#f97316', color: '#fff', border: 'none', borderRadius: '0.375rem', fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer' };
const secondaryBtnStyle: React.CSSProperties = { padding: '0.375rem 0.75rem', backgroundColor: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.8rem', fontWeight: 500, cursor: 'pointer' };
